import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { login, signup } from "../lib/auth";
import { ApiError } from "../lib/api";
import { Loader2 } from "lucide-react";

type Mode = "signin" | "signup";

const X_ERROR_MESSAGES: Record<string, string> = {
  state_expired: "Your X sign-in attempt expired. Please try again.",
  missing_code: "X didn't return an authorization code. Please try again.",
  token_exchange: "Couldn't complete sign-in with X. Please try again.",
  userinfo: "Couldn't read your X profile. Please try again.",
  user_create: "We couldn't create your account. Please contact support.",
  access_denied: "You declined the X sign-in.",
};

export default function LoginPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [mode, setMode] = useState<Mode>("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    const xErr = searchParams.get("x_error");
    const xDetail = searchParams.get("x_detail");
    if (xErr) {
      const base = X_ERROR_MESSAGES[xErr] ?? `X sign-in failed (${xErr}).`;
      setErrorMsg(xDetail ? `${base} — ${xDetail}` : base);
      const next = new URLSearchParams(searchParams);
      next.delete("x_error");
      next.delete("x_detail");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  function setModeAndClear(next: Mode) {
    setMode(next);
    setErrorMsg("");
  }

  function startXAuth() {
    const next = searchParams.get("next") || "/app";
    const url = `/api/auth/x/start?next=${encodeURIComponent(next)}`;
    window.location.href = url;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email || !password) return;
    if (mode === "signup" && password.length < 8) {
      setErrorMsg("Password must be at least 8 characters.");
      return;
    }
    setBusy(true);
    setErrorMsg("");
    try {
      if (mode === "signup") {
        await signup(email, password);
      } else {
        await login(email, password);
      }
      navigate("/app", { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 401) {
          setErrorMsg("Wrong email or password.");
        } else if (err.status === 409) {
          setErrorMsg("Email already registered. Try signing in instead.");
        } else if (err.status === 400) {
          setErrorMsg("Please check your email and password.");
        } else {
          setErrorMsg(`Something went wrong (${err.status}).`);
        }
      } else {
        setErrorMsg(err instanceof Error ? err.message : "Unknown error");
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-ink-50 px-6 py-10">
      <div className="w-full max-w-sm">
        <Link
          to="/"
          className="mb-8 block text-center text-lg font-semibold text-ink-900 no-underline"
        >
          BrandEcho
        </Link>
        <div className="card">
          <h1 className="text-xl font-semibold text-ink-900">
            {mode === "signin" ? "Welcome back" : "Create your account"}
          </h1>
          <p className="mt-1 text-sm text-ink-500">
            {mode === "signin"
              ? "Sign in to BrandEcho."
              : "Start your 7-day free trial."}
          </p>

          <button
            type="button"
            onClick={startXAuth}
            className="mt-5 flex w-full items-center justify-center gap-2.5 rounded-lg bg-black px-4 py-2.5 text-sm font-medium text-white transition hover:bg-ink-900"
          >
            <XIcon className="h-4 w-4" />
            {mode === "signin" ? "Sign in with X" : "Continue with X"}
          </button>

          <div className="my-5 flex items-center gap-3 text-2xs uppercase tracking-wider text-ink-400">
            <span className="h-px flex-1 bg-ink-200" />
            <span>or</span>
            <span className="h-px flex-1 bg-ink-200" />
          </div>

          {!showEmailForm ? (
            <button
              type="button"
              onClick={() => setShowEmailForm(true)}
              className="block w-full text-center text-xs text-ink-500 underline-offset-2 hover:text-ink-700 hover:underline"
            >
              {mode === "signin"
                ? "Sign in with email instead"
                : "Use email instead"}
            </button>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-3">
              <div>
                <label className="mb-1 block text-xs font-medium text-ink-700">
                  Email
                </label>
                <input
                  className="input"
                  type="email"
                  required
                  autoFocus
                  placeholder="you@domain.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <div>
                <div className="mb-1 flex items-center justify-between">
                  <label className="block text-xs font-medium text-ink-700">
                    Password
                  </label>
                  {mode === "signin" ? (
                    <Link
                      to="/forgot-password"
                      className="text-2xs font-medium text-brand-600 no-underline hover:text-brand-700 hover:underline"
                    >
                      Forgot?
                    </Link>
                  ) : null}
                </div>
                <input
                  className="input"
                  type="password"
                  required
                  minLength={mode === "signup" ? 8 : undefined}
                  placeholder={
                    mode === "signup" ? "At least 8 characters" : "Your password"
                  }
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
              <button
                type="submit"
                className="btn-secondary w-full"
                disabled={busy}
              >
                {busy ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {mode === "signup" ? "Creating..." : "Signing in..."}
                  </>
                ) : mode === "signup" ? (
                  "Create account"
                ) : (
                  "Sign in"
                )}
              </button>
            </form>
          )}

          {errorMsg ? (
            <p className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
              {errorMsg}
            </p>
          ) : null}

          <p className="mt-5 border-t border-ink-100 pt-4 text-center text-xs text-ink-500">
            {mode === "signin" ? (
              <>
                New here?{" "}
                <button
                  type="button"
                  onClick={() => setModeAndClear("signup")}
                  className="font-medium text-brand-600 hover:underline"
                >
                  Create an account
                </button>
              </>
            ) : (
              <>
                Already have an account?{" "}
                <button
                  type="button"
                  onClick={() => setModeAndClear("signin")}
                  className="font-medium text-brand-600 hover:underline"
                >
                  Sign in
                </button>
              </>
            )}
          </p>
        </div>
        <p className="mt-4 text-center text-2xs text-ink-400">
          By continuing you agree to our Terms and Privacy Policy.
        </p>
      </div>
    </div>
  );
}

function XIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
      className={className}
    >
      <path d="M18.244 2H21.55l-7.222 8.252L22.83 22h-6.66l-5.214-6.815L4.989 22H1.68l7.722-8.825L1.17 2h6.83l4.713 6.231L18.244 2zm-1.166 18.082h1.832L7.027 3.83H5.075l12.003 16.252z" />
    </svg>
  );
}
